import * as db from '@/adapters/db';
import { enqueueJob } from '@/adapters/queue';

export async function getProductByReviewToken(token: string) {
  return db.getProductByVendorToken(token);
}

export async function getReviewIteration(productId: string, iterationNumber?: number) {
  const product = await db.getProductById(productId);
  const currentIteration = iterationNumber || product?.current_review_iteration || 1;
  
  const iteration = await db.getReviewIteration(productId, currentIteration);
  const pins = await db.getProductPins(productId, currentIteration);
  
  return { iteration, pins, currentIteration };
}

export async function createPin(params: {
  product_id: string;
  x_percent: number;
  y_percent: number;
  text: string;
  author_name?: string;
  author_email?: string;
}) {
  const product = await db.getProductById(params.product_id);
  const pin = await db.createProductPin({
    ...params,
    iteration_number: product?.current_review_iteration || 1,
    author_type: 'vendor',
  });
  
  // Update iteration stats
  await db.incrementIterationPinsCount(
    params.product_id,
    product?.current_review_iteration || 1
  );
  
  // Update product status if first pin
  await db.updateProduct(params.product_id, {
    vendor_review_status: 'feedback_received',
    status: 'vendor_reviewing',
  });
  
  return pin;
}

export async function sendToVendorForReview(productId: string) {
  const product = await db.getProductById(productId);
  
  if (!product?.vendor_email) {
    throw new Error('NO_VENDOR_EMAIL');
  }
  
  if (product.status !== 'internal_review' && product.status !== 'ai_generated') {
    throw new Error('Product must be in internal_review status');
  }
  
  // Generate review token if not exists
  let reviewToken = product.vendor_review_token;
  if (!reviewToken) {
    reviewToken = crypto.randomUUID();
    await db.updateProduct(productId, { vendor_review_token: reviewToken });
  }
  
  // Create first iteration
  const iterationNumber = (product.current_review_iteration || 0) + 1;
  await db.createReviewIteration(productId, iterationNumber);
  
  // Update product
  await db.updateProduct(productId, {
    status: 'sent_to_vendor',
    vendor_review_status: 'sent',
    vendor_sent_at: new Date().toISOString(),
    current_review_iteration: iterationNumber,
  });
  
  // Enqueue email job (async)
  return enqueueJob({
    type: 'send-vendor-review-email',
    payload: { productId, iterationNumber },
  });
}

export async function sendUpdatesToVendor(productId: string) {
  const product = await db.getProductById(productId);
  
  if (!product) throw new Error('Product not found');
  
  // Create new iteration
  const newIteration = (product.current_review_iteration || 1) + 1;
  await db.createReviewIteration(productId, newIteration);
  
  // Update product
  await db.updateProduct(productId, {
    status: 'awaiting_vendor',
    vendor_review_status: 'updates_sent',
    current_review_iteration: newIteration,
  });
  
  // Send email
  return enqueueJob({
    type: 'send-vendor-update-email',
    payload: { productId, iterationNumber: newIteration },
  });
}

export async function approveByVendor(productId: string, approverEmail: string) {
  // Update product - auto-publish on vendor approval
  await db.updateProduct(productId, {
    status: 'published',
    vendor_review_status: 'approved',
    vendor_approved_at: new Date().toISOString(),
    approved_by_email: approverEmail,
    is_published: true,
  });
}

export async function resolvePin(pinId: string, userId: string, notes?: string) {
  return db.updatePin(pinId, {
    status: 'resolved',
    resolved: true,
    resolved_at: new Date().toISOString(),
    resolved_by: userId,
    resolution_notes: notes,
  });
}

export async function markIterationViewed(productId: string, iterationNumber: number) {
  await db.updateReviewIteration(productId, iterationNumber, {
    vendor_viewed_at: new Date().toISOString(),
    status: 'viewed',
  });
  
  await db.updateProduct(productId, {
    vendor_last_viewed_at: new Date().toISOString(),
    vendor_review_status: 'viewed',
  });
}

export async function markAsInternalReview(productId: string) {
  return db.updateProduct(productId, { status: 'internal_review' });
}

export async function markAsUpdatesInProgress(productId: string) {
  return db.updateProduct(productId, { status: 'updates_in_progress' });
}
